/**
 * V94-3-1: Batch taxonomy classifier for jurisdiction rules.
 *
 * Backfills `rule_domains` and `risk_vectors` (ADR-0042) on existing
 * jurisdiction rules that predate the extraction prompt addition (V94-2-1).
 * Uses the AI provider with a focused prompt that only extracts domain
 * and risk-vector tags — it does NOT re-extract predicates or obligations.
 *
 * Pure core service — no Cloudflare imports.
 */
import { defineTemplate, field } from '../egress/data-classes.js';
import { RULE_DOMAINS, RULE_DOMAIN_LABELS, RISK_VECTORS, RISK_VECTOR_LABELS, } from '../vocabularies/index.js';
// ---- Constants ------------------------------------------------------------
const RULE_DOMAINS_SET = new Set(RULE_DOMAINS);
const RISK_VECTORS_SET = new Set(RISK_VECTORS);
function formatEnumList(values, labels) {
    return values.map((v) => `  ${v} — ${labels[v] ?? v}`).join('\n');
}
/** Filter an LLM-returned array to only values present in the allowed set. */
function normalizeEnumArray(raw, allowed) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const v of raw) {
        if (typeof v === 'string' && allowed.has(v) && !out.includes(v)) {
            out.push(v);
        }
    }
    return out;
}
// ---- Prompt ---------------------------------------------------------------
const CLASSIFY_PROMPT = [
    'You are classifying an existing jurisdiction rule into domain and risk-vector categories.',
    '',
    'STATUTE: {statute}',
    'CITATION: {citation}',
    'JURISDICTION: {jurisdiction}',
    'OBLIGATIONS: {obligations}',
    '',
    'Respond ONLY with a JSON object:',
    '{',
    '  "rule_domains": ["1-3 values from RULE_DOMAINS below"],',
    '  "risk_vectors": ["1-4 values from RISK_VECTORS below"],',
    '  "confidence": <0.0-1.0>',
    '}',
    '',
    'RULE_DOMAINS (pick 1-3 that describe the sector/domain this rule regulates):',
    '{rule_domain_list}',
    '',
    'RISK_VECTORS (pick 1-4 risks this rule is designed to mitigate):',
    '{risk_vector_list}',
    '',
    'Rules:',
    '- Use ONLY values from the RULE_DOMAINS and RISK_VECTORS lists above.',
    '- confidence 1.0 = clearly maps to the domains/vectors; 0.5 = partially maps.',
    '- Return raw JSON only, no markdown fences.',
].join('\n');
/** ADR-0073: platform scope; rule text and vocabularies are reference content. */
const CLASSIFY_INPUT_TEMPLATE = defineTemplate('rules.classify_taxonomy.input', 'v1', CLASSIFY_PROMPT, ['statute', 'citation', 'jurisdiction', 'obligations', 'rule_domain_list', 'risk_vector_list']);
const CLASSIFY_TEMPLATE = defineTemplate('rules.classify_taxonomy', 'v1', '', []);
const CLASSIFY_SCHEMA = {
    rule_domains: ['string'],
    risk_vectors: ['string'],
    confidence: 'number (0.0-1.0)',
};
/**
 * Classify a single jurisdiction rule's domain + risk-vector tags using the
 * AI provider. Does NOT re-extract predicates or obligations.
 */
export async function classifyRuleTaxonomy(db, llm, ruleId, opts = {}) {
    const res = await db.query('SELECT rule_id, jurisdiction, statute, citation, obligations_json, rule_domains, risk_vectors FROM jurisdiction_rules WHERE rule_id = ?', [ruleId]);
    const rule = res.results[0];
    if (!rule)
        throw new Error(`jurisdiction_rule ${ruleId} not found`);
    // Build obligations summary from JSON
    let obligationsSummary = '';
    try {
        const obls = JSON.parse(rule.obligations_json || '[]');
        obligationsSummary = obls.map((o) => `${o.title ?? ''} (${o.duty ?? ''}, ${o.citation ?? ''})`).join('; ');
    }
    catch {
        obligationsSummary = rule.obligations_json || '';
    }
    const rt = (v) => field('rules.rule_text', v);
    const extraction = await llm.run({
        task: 'rules.classify_taxonomy', template: CLASSIFY_TEMPLATE, inputTemplate: CLASSIFY_INPUT_TEMPLATE,
        schemaHint: CLASSIFY_SCHEMA,
        slots: {
            statute: rt(rule.statute), citation: rt(rule.citation), jurisdiction: rt(rule.jurisdiction), obligations: rt(obligationsSummary),
            rule_domain_list: field('rules.vocabulary', formatEnumList(RULE_DOMAINS, RULE_DOMAIN_LABELS)),
            risk_vector_list: field('rules.vocabulary', formatEnumList(RISK_VECTORS, RISK_VECTOR_LABELS)),
        },
    });
    if (extraction.status !== 'ok')
        throw new Error('No AI provider can classify rules here');
    const ruleDomains = normalizeEnumArray(extraction.output.rule_domains, RULE_DOMAINS_SET);
    const riskVectors = normalizeEnumArray(extraction.output.risk_vectors, RISK_VECTORS_SET);
    const confidence = typeof extraction.output.confidence === 'number'
        ? Math.max(0, Math.min(1, extraction.output.confidence))
        : extraction.confidence;
    return { rule_domains: ruleDomains, risk_vectors: riskVectors, confidence };
}
/**
 * V94-3-2: Batch-classify all jurisdiction rules that have empty taxonomy
 * metadata. Idempotent — rules already classified are skipped. Sequential
 * to respect AI provider rate limits.
 */
export async function batchClassifyRuleTaxonomy(db, llm, opts = {}) {
    const limit = Math.max(1, Math.min(opts.limit ?? 20, 100));
    const candidates = await db.query(`SELECT rule_id FROM jurisdiction_rules
      WHERE (rule_domains IS NULL OR rule_domains = '[]')
        AND (risk_vectors IS NULL OR risk_vectors = '[]')
      ORDER BY rule_id ASC
      LIMIT ?`, [limit]);
    const out = { attempted: 0, updated: 0, skipped: 0, results: [] };
    for (const row of candidates.results) {
        out.attempted++;
        try {
            const result = await classifyRuleTaxonomy(db, llm, row.rule_id);
            if (result.rule_domains.length === 0 && result.risk_vectors.length === 0) {
                out.skipped++;
                out.results.push({ rule_id: row.rule_id, ...result, error: 'no domains or vectors returned' });
                continue;
            }
            await db.execute(`UPDATE jurisdiction_rules
          SET rule_domains = ?, risk_vectors = ?
          WHERE rule_id = ?`, [JSON.stringify(result.rule_domains), JSON.stringify(result.risk_vectors), row.rule_id]);
            out.updated++;
            out.results.push({ rule_id: row.rule_id, ...result });
        }
        catch (err) {
            out.skipped++;
            out.results.push({
                rule_id: row.rule_id,
                rule_domains: [],
                risk_vectors: [],
                confidence: 0,
                error: err instanceof Error ? err.message : String(err),
            });
        }
    }
    return out;
}
//# sourceMappingURL=rule-taxonomy-classifier.js.map